import React, { useEffect, useRef, useState, useCallback } from "react";
import { MapPin, Navigation, Loader2, AlertTriangle } from "lucide-react";

// Add this type declaration
declare global {
  interface Window {
    google: any;
  }
  const google: any;
}

// Types for our map component
export interface MapLocation {
  id: string;
  lat: number;
  lng: number;
  title: string;
  description?: string;
  type?: "site" | "subsite" | "device";
  address?: string;
  icon?: "site" | "subsite" | "device" | "custom";
}

export interface GoogleMapProps {
  locations?: MapLocation[];
  center?: { lat: number; lng: number };
  zoom?: number;
  height?: string;
  width?: string;
  className?: string;
  showControls?: boolean;
  onLocationClick?: (location: MapLocation) => void;
  onMapClick?: (lat: number, lng: number) => void;
  apiKey?: string;
  loading?: boolean;
  error?: string;
}

// Custom marker icons for different types
const getMarkerIcon = (type: string = "site") => {
  const icons = {
    site: {
      path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
      fillColor: "#3B82F6", // Blue
      fillOpacity: 1,
      strokeColor: "#1E40AF",
      strokeWeight: 2,
      scale: 1.5,
    },
    subsite: {
      path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
      fillColor: "#10B981", // Green
      fillOpacity: 1,
      strokeColor: "#059669",
      strokeWeight: 2,
      scale: 1.2,
    },
    device: {
      path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
      fillColor: "#F59E0B", // Orange
      fillOpacity: 1,
      strokeColor: "#D97706",
      strokeWeight: 2,
      scale: 1,
    },
    custom: {
      path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
      fillColor: "#EF4444", // Red for alerts/issues
      fillOpacity: 1,
      strokeColor: "#DC2626",
      strokeWeight: 2,
      scale: 1.5,
    },
  };
  return icons[type] || icons.site;
};

const GoogleMap: React.FC<GoogleMapProps> = ({
  locations = [],
  center = { lat: 37.7749, lng: -122.4194 }, // Default to San Francisco
  zoom = 10,
  height = "400px",
  width = "100%",
  className = "",
  showControls = true,
  onLocationClick,
  onMapClick,
  apiKey,
  loading = false,
  error,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const [mapError, setMapError] = useState<string>("");

  // Check if Google Maps is already loaded
  const checkGoogleMaps = useCallback(() => {
    return typeof google !== "undefined" && google.maps;
  }, []);

  // Load Google Maps API
  const loadGoogleMaps = useCallback(async () => {
    if (checkGoogleMaps()) {
      setIsGoogleLoaded(true);
      return;
    }

    if (!apiKey) {
      setMapError("Google Maps API key is required");
      return;
    }

    try {
      // Create script element
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;

      // Handle script load
      script.onload = () => {
        setIsGoogleLoaded(true);
        setMapError("");
      };

      script.onerror = () => {
        setMapError("Failed to load Google Maps");
      };

      document.head.appendChild(script);
    } catch (err) {
      setMapError("Error loading Google Maps API");
    }
  }, [apiKey, checkGoogleMaps]);

  // Initialize the map (only once)
  const initializeMap = useCallback(() => {
    if (!mapRef.current || !checkGoogleMaps() || mapInstanceRef.current) return;

    try {
      const mapOptions: google.maps.MapOptions = {
        center,
        zoom,
        mapTypeControl: showControls,
        streetViewControl: showControls,
        fullscreenControl: showControls,
        zoomControl: showControls,
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }],
          },
        ],
      };

      mapInstanceRef.current = new google.maps.Map(mapRef.current, mapOptions);

      // Add click listener if callback provided
      if (onMapClick) {
        mapInstanceRef.current.addListener(
          "click",
          (event: google.maps.MapMouseEvent) => {
            if (event.latLng) {
              onMapClick(event.latLng.lat(), event.latLng.lng());
            }
          }
        );
      }
    } catch (err) {
      setMapError("Failed to initialize map");
    }
  }, [center, zoom, showControls, onMapClick, checkGoogleMaps]);

  // Clear existing markers
  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];
  }, []);

  // Add markers to the map
  const addMarkers = useCallback(() => {
    if (!mapInstanceRef.current || !checkGoogleMaps()) return;

    clearMarkers();

    locations.forEach((location) => {
      try {
        const marker = new google.maps.Marker({
          position: { lat: location.lat, lng: location.lng },
          map: mapInstanceRef.current,
          title: location.title,
          icon: getMarkerIcon(location.icon || location.type),
        });

        // Add click listener - no InfoWindow popup, just trigger callback
        marker.addListener("click", () => {
          if (onLocationClick) {
            onLocationClick(location);
          }
        });
        markersRef.current.push(marker);
      } catch (err) {
        console.error("Error creating marker:", err);
      }
    });

    // Adjust map bounds to fit all markers
    if (locations.length > 1) {
      const bounds = new google.maps.LatLngBounds();
      locations.forEach((location) => {
        bounds.extend({ lat: location.lat, lng: location.lng });
      });
      mapInstanceRef.current.fitBounds(bounds);
    }
  }, [locations, onLocationClick, clearMarkers, checkGoogleMaps]);

  // Load Google Maps on mount
  useEffect(() => {
    loadGoogleMaps();
  }, [loadGoogleMaps]);

  // Initialize map when Google Maps is loaded (only once)
  useEffect(() => {
    if (isGoogleLoaded && !mapInstanceRef.current) {
      initializeMap();
    }
  }, [isGoogleLoaded, initializeMap]);

  // Update markers when locations change
  useEffect(() => {
    if (isGoogleLoaded && mapInstanceRef.current) {
      addMarkers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGoogleLoaded, locations]);

  // Update center and zoom when props change (but don't re-initialize)
  useEffect(() => {
    if (mapInstanceRef.current) {
      const currentCenter = mapInstanceRef.current.getCenter();
      const currentZoom = mapInstanceRef.current.getZoom();
      
      // Only update if actually changed to avoid unnecessary updates
      if (currentCenter && (currentCenter.lat() !== center.lat || currentCenter.lng() !== center.lng)) {
        mapInstanceRef.current.setCenter(center);
      }
      if (currentZoom !== zoom) {
        mapInstanceRef.current.setZoom(zoom);
      }
    }
  }, [center, zoom]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearMarkers();
    };
  }, [clearMarkers]);

  // Render loading state
  if (loading || (!isGoogleLoaded && !mapError && !error)) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 rounded-lg ${className}`}
        style={{ height, width }}
      >
        <div className="flex flex-col items-center space-y-2">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          <span className="text-sm text-gray-600">Loading map...</span>
        </div>
      </div>
    );
  }

  // Render error state
  if (error || mapError) {
    return (
      <div
        className={`flex items-center justify-center bg-red-50 border border-red-200 rounded-lg ${className}`}
        style={{ height, width }}
      >
        <div className="flex flex-col items-center space-y-2 p-4 text-center">
          <AlertTriangle className="w-6 h-6 text-red-600" />
          <span className="text-sm text-red-700">{error || mapError}</span>
          <span className="text-xs text-red-500">
            Please check your Google Maps API configuration
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative rounded-lg overflow-hidden border border-gray-200 ${className}`}
      style={{ height, width }}
    >
      <div ref={mapRef} className="w-full h-full" />

      {/* Map overlay controls */}
      {showControls && (
        <div className="absolute top-2 right-2 bg-white rounded-lg shadow-md p-2">
          <div className="flex flex-col space-y-1">
            <div className="flex items-center space-x-1 text-xs text-gray-600">
              <MapPin className="w-3 h-3" />
              <span>
                {locations.length} location{locations.length !== 1 ? "s" : ""}
              </span>
            </div>
            {locations.length > 0 && (
              <button
                onClick={() => {
                  if (mapInstanceRef.current && locations.length > 1) {
                    const bounds = new google.maps.LatLngBounds();
                    locations.forEach((location) => {
                      bounds.extend({ lat: location.lat, lng: location.lng });
                    });
                    mapInstanceRef.current.fitBounds(bounds);
                  }
                }}
                className="flex items-center space-x-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                <Navigation className="w-3 h-3" />
                <span>Fit All</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleMap;
